import "./Footer.css";
import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
  IconWhatsapp,
  IconFacebook,
  IconInstagram,
  IconTikTok,
  IconYoutube,
  IconX,
  IconMail,
  IconPhone,
} from "../../icons/Icons";
import { getStoreFooterBranding } from "../../services/public/Branding";
import { fileURL } from "../../utils/fileURL";

const DEFAULT_FOOTER_BRANDING = {
  nombre_tienda: "Cyravi Store",
  slogan: "Tu tienda online para tu negocio",
  logo_url: "/image/cyravi-default-logo.png",

  contacto: {
    whatsapp_url: "https://wa.me/50252472763",
    whatsapp_nombre_visible: null,
  },

  redes_sociales: {
    facebook_url: "https://facebook.com/",
    facebook_nombre_visible: null,
    instagram_url: "https://www.instagram.com/",
    instagram_nombre_visible: null,
    tiktok_url: "https://www.tiktok.com/",
    tiktok_nombre_visible: null,
  },

  metodos_pago: [],

  documentos_legales: [],

  canales_siguenos: [],
  canales_contacto_footer: [],

  tema: {
    color_primario: "#818cf8",
    color_secundario: "#22d3ee",

    color_texto_primario: "#a5b4fc",
    color_texto_secundario: "#f1f5f9",
    color_texto_terciario: "#94a3b8",
    color_texto_contraste: "#ffffff",

    color_borde_primario: "rgba(248, 250, 252, 0.08)",
    color_borde_fuerte: "rgba(248, 250, 252, 0.16)",

    color_fondo_primario: "#0f172a",
    color_fondo_secundario: "#1e293b",
    color_fondo_terciario: "rgba(255, 255, 255, 0.05)",
    color_fondo_cuaternario: "rgba(99, 102, 241, 0.12)",
    color_fondo_quinario: "rgba(34, 211, 238, 0.08)",
    color_fondo_senario: "rgba(15, 23, 42, 0.6)",

    color_resplandor_izquierdo: "rgba(99, 102, 241, 0.15)",
    color_resplandor_derecho: "rgba(34, 211, 238, 0.12)",
    color_anillo_enfoque: "rgba(129, 140, 248, 0.35)",

    sombra_suave: "0 12px 32px rgba(0, 0, 0, 0.35)",
    sombra_principal: "0 24px 48px rgba(0, 0, 0, 0.45)",
    sombra_boton_primario: "0 8px 24px rgba(99, 102, 241, 0.45)",
    sombra_boton_primario_hover: "0 12px 28px rgba(99, 102, 241, 0.55)",
  },
};

function footerIconForCanal(codigo) {
  const c = String(codigo || "").toUpperCase();
  switch (c) {
    case "FACEBOOK":
      return IconFacebook;
    case "INSTAGRAM":
      return IconInstagram;
    case "WHATSAPP":
      return IconWhatsapp;
    case "YOUTUBE":
      return IconYoutube;
    case "X":
      return IconX;
    case "EMAIL":
      return IconMail;
    case "TELEFONO":
      return IconPhone;
    default:
      return IconTikTok;
  }
}

function FooterCanalLink({ item }) {
  const codigo = String(item.codigo || "").toUpperCase();
  const href = String(item.link_href || "#").trim() || "#";
  const label = String(item.nombre_mostrar || item.tipo_nombre_visible || codigo).trim();
  const external = /^https?:\/\//i.test(href);
  return (
    <a
      href={href}
      className="cyravi-footer__social"
      data-canal={codigo}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      aria-label={`${codigo} · ${label}`}
    >
      <span className="cyravi-footer__icon-box">{footerIconForCanal(codigo)}</span>
      <span className="cyravi-footer__social-copy">
        <span className="cyravi-footer__social-platform">{codigo}</span>
        <span className="cyravi-footer__social-name">{label || "—"}</span>
      </span>
    </a>
  );
}

function Footer() {
  const [footerBranding, setFooterBranding] = useState(DEFAULT_FOOTER_BRANDING);

  useEffect(() => {
    let isMounted = true;

    const loadFooterBranding = async () => {
      try {
        const res = await getStoreFooterBranding();

        if (!isMounted) return;

        const data = res?.data || res || {};
        const apiContacto = data?.contacto;
        const apiRedes = data?.redes_sociales;
        const apiMetodosPago = Array.isArray(data?.metodos_pago)
          ? data.metodos_pago
              .map((m, index) => ({
                tienda_metodo_pago_id: Number(m?.tienda_metodo_pago_id) || index,
                nombre_visible: String(m?.nombre_visible || "").trim(),
              }))
              .filter((m) => m.nombre_visible.length > 0)
          : [];

        const apiDocumentosLegales = Array.isArray(data?.documentos_legales)
          ? data.documentos_legales
              .map((d) => ({
                codigo: String(d?.codigo || "").trim(),
                etiqueta: String(d?.etiqueta || "").trim(),
                titulo: String(d?.titulo || "").trim(),
                slug: String(d?.slug || "").trim(),
              }))
              .filter((d) => d.codigo && d.etiqueta && d.slug)
          : [];

        const apiCanalesSiguenos = Array.isArray(data?.canales_siguenos)
          ? data.canales_siguenos
          : [];
        const apiCanalesContactoFooter = Array.isArray(data?.canales_contacto_footer)
          ? data.canales_contacto_footer
          : [];

        setFooterBranding({
          nombre_tienda:
            data.nombre_tienda ||
            data.companyName ||
            data.storeName ||
            data.nombreComercial ||
            data.nombre_comercial ||
            DEFAULT_FOOTER_BRANDING.nombre_tienda,

          slogan: data.slogan || DEFAULT_FOOTER_BRANDING.slogan,

          logo_url:
            data.logo_url ||
            data.logoUrl ||
            data.logoPrincipalUrl ||
            data.logo_principal_url ||
            DEFAULT_FOOTER_BRANDING.logo_url,

          contacto: {
            whatsapp_url:
              apiContacto != null && "whatsapp_url" in apiContacto
                ? apiContacto.whatsapp_url
                : data?.contact?.whatsappUrl ||
                  data?.whatsappUrl ||
                  data?.whatsapp_url ||
                  DEFAULT_FOOTER_BRANDING.contacto.whatsapp_url,
            whatsapp_nombre_visible:
              apiContacto != null && "whatsapp_nombre_visible" in apiContacto
                ? apiContacto.whatsapp_nombre_visible
                : DEFAULT_FOOTER_BRANDING.contacto.whatsapp_nombre_visible,
          },

          redes_sociales: {
            facebook_url:
              apiRedes != null && "facebook_url" in apiRedes
                ? apiRedes.facebook_url
                : data?.social?.facebookUrl ||
                  data?.facebookUrl ||
                  data?.facebook_url ||
                  DEFAULT_FOOTER_BRANDING.redes_sociales.facebook_url,
            facebook_nombre_visible:
              apiRedes != null && "facebook_nombre_visible" in apiRedes
                ? apiRedes.facebook_nombre_visible
                : DEFAULT_FOOTER_BRANDING.redes_sociales.facebook_nombre_visible,

            instagram_url:
              apiRedes != null && "instagram_url" in apiRedes
                ? apiRedes.instagram_url
                : data?.social?.instagramUrl ||
                  data?.instagramUrl ||
                  data?.instagram_url ||
                  DEFAULT_FOOTER_BRANDING.redes_sociales.instagram_url,
            instagram_nombre_visible:
              apiRedes != null && "instagram_nombre_visible" in apiRedes
                ? apiRedes.instagram_nombre_visible
                : DEFAULT_FOOTER_BRANDING.redes_sociales.instagram_nombre_visible,

            tiktok_url:
              apiRedes != null && "tiktok_url" in apiRedes
                ? apiRedes.tiktok_url
                : data?.social?.tiktokUrl ||
                  data?.tiktokUrl ||
                  data?.tiktok_url ||
                  DEFAULT_FOOTER_BRANDING.redes_sociales.tiktok_url,
            tiktok_nombre_visible:
              apiRedes != null && "tiktok_nombre_visible" in apiRedes
                ? apiRedes.tiktok_nombre_visible
                : DEFAULT_FOOTER_BRANDING.redes_sociales.tiktok_nombre_visible,
          },

          metodos_pago: apiMetodosPago,

          documentos_legales: apiDocumentosLegales,

          canales_siguenos: apiCanalesSiguenos,
          canales_contacto_footer: apiCanalesContactoFooter,

          tema: {
            color_primario:
              data?.tema?.color_primario ||
              data?.theme?.primaryColor ||
              data?.theme?.primary_color ||
              DEFAULT_FOOTER_BRANDING.tema.color_primario,

            color_secundario:
              data?.tema?.color_secundario ||
              data?.theme?.secondaryColor ||
              data?.theme?.secondary_color ||
              DEFAULT_FOOTER_BRANDING.tema.color_secundario,

            color_texto_primario:
              data?.tema?.color_texto_primario ||
              data?.theme?.textColorPrimary ||
              data?.theme?.text_color_primary ||
              DEFAULT_FOOTER_BRANDING.tema.color_texto_primario,

            color_texto_secundario:
              data?.tema?.color_texto_secundario ||
              data?.theme?.textColorSecondary ||
              data?.theme?.text_color_secondary ||
              DEFAULT_FOOTER_BRANDING.tema.color_texto_secundario,

            color_texto_terciario:
              data?.tema?.color_texto_terciario ||
              data?.theme?.textColorTertiary ||
              data?.theme?.text_color_tertiary ||
              DEFAULT_FOOTER_BRANDING.tema.color_texto_terciario,

            color_texto_contraste:
              data?.tema?.color_texto_contraste ||
              data?.theme?.textColorQuaternary ||
              data?.theme?.text_color_quaternary ||
              DEFAULT_FOOTER_BRANDING.tema.color_texto_contraste,

            color_borde_primario:
              data?.tema?.color_borde_primario ||
              data?.theme?.borderColorPrimary ||
              data?.theme?.border_color_primary ||
              DEFAULT_FOOTER_BRANDING.tema.color_borde_primario,

            color_borde_fuerte:
              data?.tema?.color_borde_fuerte ||
              data?.theme?.borderColorStrong ||
              data?.theme?.border_color_strong ||
              DEFAULT_FOOTER_BRANDING.tema.color_borde_fuerte,

            color_fondo_primario:
              data?.tema?.color_fondo_primario ||
              data?.theme?.backgroundColorPrimary ||
              data?.theme?.background_color_primary ||
              DEFAULT_FOOTER_BRANDING.tema.color_fondo_primario,

            color_fondo_secundario:
              data?.tema?.color_fondo_secundario ||
              data?.theme?.backgroundColorSecondary ||
              data?.theme?.background_color_secondary ||
              DEFAULT_FOOTER_BRANDING.tema.color_fondo_secundario,

            color_fondo_terciario:
              data?.tema?.color_fondo_terciario ||
              data?.theme?.backgroundColorTertiary ||
              data?.theme?.background_color_tertiary ||
              DEFAULT_FOOTER_BRANDING.tema.color_fondo_terciario,

            color_fondo_cuaternario:
              data?.tema?.color_fondo_cuaternario ||
              data?.theme?.backgroundColorQuaternary ||
              data?.theme?.background_color_quaternary ||
              DEFAULT_FOOTER_BRANDING.tema.color_fondo_cuaternario,

            color_fondo_quinario:
              data?.tema?.color_fondo_quinario ||
              data?.theme?.backgroundColorQuinary ||
              data?.theme?.background_color_quinary ||
              DEFAULT_FOOTER_BRANDING.tema.color_fondo_quinario,

            color_fondo_senario:
              data?.tema?.color_fondo_senario ||
              data?.theme?.backgroundColorSenary ||
              data?.theme?.background_color_senary ||
              DEFAULT_FOOTER_BRANDING.tema.color_fondo_senario,

            color_resplandor_izquierdo:
              data?.tema?.color_resplandor_izquierdo ||
              data?.theme?.glowLeft ||
              DEFAULT_FOOTER_BRANDING.tema.color_resplandor_izquierdo,

            color_resplandor_derecho:
              data?.tema?.color_resplandor_derecho ||
              data?.theme?.glowRight ||
              DEFAULT_FOOTER_BRANDING.tema.color_resplandor_derecho,

            color_anillo_enfoque:
              data?.tema?.color_anillo_enfoque ||
              data?.theme?.searchFocusRing ||
              DEFAULT_FOOTER_BRANDING.tema.color_anillo_enfoque,

            sombra_suave:
              data?.tema?.sombra_suave ||
              data?.theme?.shadowSoft ||
              DEFAULT_FOOTER_BRANDING.tema.sombra_suave,

            sombra_principal:
              data?.tema?.sombra_principal ||
              data?.theme?.shadowMain ||
              DEFAULT_FOOTER_BRANDING.tema.sombra_principal,

            sombra_boton_primario:
              data?.tema?.sombra_boton_primario ||
              data?.theme?.cartShadow ||
              DEFAULT_FOOTER_BRANDING.tema.sombra_boton_primario,

            sombra_boton_primario_hover:
              data?.tema?.sombra_boton_primario_hover ||
              data?.theme?.cartShadowHover ||
              DEFAULT_FOOTER_BRANDING.tema.sombra_boton_primario_hover,
          },
        });
      } catch (error) {
        console.error("Error cargando branding del footer:", error);
      }
    };

    loadFooterBranding();

    return () => {
      isMounted = false;
    };
  }, []);

  const logoSrc = useMemo(() => {
    return fileURL(footerBranding.logo_url) || DEFAULT_FOOTER_BRANDING.logo_url;
  }, [footerBranding.logo_url]);

  const footerStyleVars = useMemo(
    () => ({
      "--sf-powered-by-from": DEFAULT_FOOTER_BRANDING.tema.color_primario,
      "--sf-powered-by-to": DEFAULT_FOOTER_BRANDING.tema.color_secundario,

      "--sf-color-primario": footerBranding.tema.color_primario,
      "--sf-color-secundario": footerBranding.tema.color_secundario,

      "--sf-color-texto-primario": footerBranding.tema.color_texto_primario,
      "--sf-color-texto-secundario": footerBranding.tema.color_texto_secundario,
      "--sf-color-texto-terciario": footerBranding.tema.color_texto_terciario,
      "--sf-color-texto-contraste": footerBranding.tema.color_texto_contraste,

      "--sf-color-borde-primario": footerBranding.tema.color_borde_primario,
      "--sf-color-borde-fuerte": footerBranding.tema.color_borde_fuerte,

      "--sf-color-fondo-primario": footerBranding.tema.color_fondo_primario,
      "--sf-color-fondo-secundario": footerBranding.tema.color_fondo_secundario,
      "--sf-color-fondo-terciario": footerBranding.tema.color_fondo_terciario,
      "--sf-color-fondo-cuaternario":
        footerBranding.tema.color_fondo_cuaternario,
      "--sf-color-fondo-quinario": footerBranding.tema.color_fondo_quinario,
      "--sf-color-fondo-senario": footerBranding.tema.color_fondo_senario,

      "--sf-color-resplandor-izquierdo":
        footerBranding.tema.color_resplandor_izquierdo,
      "--sf-color-resplandor-derecho":
        footerBranding.tema.color_resplandor_derecho,
      "--sf-color-anillo-enfoque": footerBranding.tema.color_anillo_enfoque,

      "--sf-sombra-suave": footerBranding.tema.sombra_suave,
      "--sf-sombra-principal": footerBranding.tema.sombra_principal,
      "--sf-sombra-boton-primario":
        footerBranding.tema.sombra_boton_primario,
      "--sf-sombra-boton-primario-hover":
        footerBranding.tema.sombra_boton_primario_hover,
    }),
    [footerBranding]
  );

  const handleLogoError = (e) => {
    if (e.currentTarget.dataset.fallbackApplied === "1") return;
    e.currentTarget.dataset.fallbackApplied = "1";
    e.currentTarget.src = DEFAULT_FOOTER_BRANDING.logo_url;
  };

  const metodosPagoFooter = footerBranding.metodos_pago || [];
  const documentosLegalesFooter = footerBranding.documentos_legales || [];
  const canalesSiguenos = footerBranding.canales_siguenos || [];
  const canalesContactoFooter = footerBranding.canales_contacto_footer || [];

  return (
    <div className="cyravi-footer-scope" style={footerStyleVars}>
      <footer className="cyravi-footer">
        <div className="cyravi-footer__inner">
          <div className="cyravi-footer__main">
            <div className="cyravi-footer__brand">
              <img
                className="cyravi-footer__logo"
                src={logoSrc}
                alt={footerBranding.nombre_tienda}
                onError={handleLogoError}
              />
              <p className="cyravi-footer__desc">{footerBranding.slogan}</p>
            </div>

            <div className="cyravi-footer__info-stack">
              <nav
                className="cyravi-footer__legal-col"
                aria-label="Información legal"
              >
                <span className="cyravi-footer__col-title">Información</span>
                <div className="cyravi-footer__legal-links">
                  {documentosLegalesFooter.map((d) => (
                    <Link
                      key={d.codigo}
                      to={`/store/legal/${encodeURIComponent(d.slug)}`}
                      className="cyravi-footer__legal-link"
                    >
                      {d.etiqueta}
                    </Link>
                  ))}
                </div>
              </nav>

              {metodosPagoFooter.length > 0 ? (
                <section
                  className="cyravi-footer__pagos-col"
                  aria-label="Métodos de pago disponibles"
                >
                  <span className="cyravi-footer__col-title">
                    Métodos de pago
                  </span>
                  <ul className="cyravi-footer__pagos-list">
                    {metodosPagoFooter.map((m) => (
                      <li key={m.tienda_metodo_pago_id}>
                        {m.nombre_visible}
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}
            </div>

            <div className="cyravi-footer__side">
              {canalesContactoFooter.length > 0 ? (
                <div className="cyravi-footer__card">
                  <div className="cyravi-footer__card-head">
                    <span className="cyravi-footer__card-kicker">Contacto</span>
                    <h4>Canales de contacto</h4>
                  </div>
                  <div className="cyravi-footer__socials">
                    {canalesContactoFooter.map((item) => (
                      <FooterCanalLink
                        key={item.tienda_canal_id || item.codigo}
                        item={item}
                      />
                    ))}
                  </div>
                </div>
              ) : null}

              {canalesSiguenos.length > 0 ? (
                <div className="cyravi-footer__card">
                  <div className="cyravi-footer__card-head">
                    <span className="cyravi-footer__card-kicker">Social</span>
                    <h4>Síguenos</h4>
                  </div>
                  <div className="cyravi-footer__socials">
                    {canalesSiguenos.map((item) => (
                      <FooterCanalLink
                        key={item.tienda_canal_id || item.codigo}
                        item={item}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div className="cyravi-footer__bottom">
            <span>
              © {new Date().getFullYear()} {footerBranding.nombre_tienda}
            </span>
            <span className="cyravi-footer__dot">•</span>
            <span>
              Powered by{" "}
              <a
                href="https://www.facebook.com/profile.php?id=61586295339350"
                target="_blank"
                rel="noopener noreferrer"
                className="cyravi-footer__powered"
              >
                cyravi
              </a>
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Footer;